
import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "@/components/ui/link-compat";
import { usePathname, useRouter } from "@/lib/router-compat";
import {
  Users, Handshake, CheckSquare, FolderKanban, LayoutDashboard,
  Settings, ChevronLeft, ChevronRight,
  LogOut, Check, Search, ChevronsUpDown, Inbox, MessageCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { LogoLockup } from "@/components/brand/logo";
import { preloadRoute } from "@/lib/nav-preload";
import { profileApi } from "@/lib/api/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STORAGE_KEY = "crm-sidebar-collapsed";

interface NavItem {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  exact?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { href: "/dashboard", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { href: "/dashboard/contacts", icon: Users, label: "Contacts" },
  { href: "/dashboard/deals", icon: Handshake, label: "Deals" },
  { href: "/dashboard/projects", icon: FolderKanban, label: "Projects" },
  { href: "/dashboard/tasks", icon: CheckSquare, label: "Tasks" },
];

const ADMIN_NAV_ITEMS: NavItem[] = [
  { href: "/dashboard/inbox", icon: Inbox, label: "Inbox" },
];


function isNavActive(pathname: string, item: NavItem): boolean {
  if (item.exact) return pathname === item.href;
  return pathname === item.href || pathname.startsWith(item.href + "/");
}

function isOptimisticActive(optimisticPath: string | null, item: NavItem): boolean {
  if (!optimisticPath) return false;
  if (item.exact) return optimisticPath === item.href;
  return optimisticPath === item.href || optimisticPath.startsWith(item.href + "/");
}

interface AppSidebarProps {
  currentOrg: { id: string; name: string; role: string } | null;
  organizations: { id: string; name: string; role: string }[];
  onSwitchOrg: (id: string) => Promise<void>;
  onLogout: () => void;
  onSearchOpen?: () => void;
  isSuperAdmin?: boolean;
  // Support chat entry in the account dropdown (web and native; the app has
  // no floating chat bubble).
  onChatOpen?: () => void;
}

export function AppSidebar({
  currentOrg,
  organizations,
  onSwitchOrg,
  onLogout,
  onSearchOpen,
  isSuperAdmin = false,
  onChatOpen,
}: AppSidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [optimisticPath, setOptimisticPath] = useState<string | null>(null);
  const profileQuery = useQuery({
    queryKey: ["profile"],
    queryFn: profileApi.get,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "true") setCollapsed(true);
    setMounted(true);
  }, []);

  useEffect(() => {
    setOptimisticPath(null);
  }, [pathname]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  if (!mounted) return <div className="hidden lg:block w-56 shrink-0" />;

  const orgInitial = currentOrg?.name.charAt(0).toUpperCase() ?? "?";
  const profile = profileQuery.data;
  const displayName = profile?.displayName?.trim() || currentOrg?.name || "Account";
  const userEmail = profile?.email ?? "";
  const userInitials =
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || orgInitial;

  return (
    <aside
      className={cn(
        "hidden lg:flex flex-col h-dvh border-r bg-background shrink-0 overflow-hidden transition-[width] duration-200 ease-in-out",
        collapsed ? "w-14" : "w-56"
      )}
    >
      {/* Logo + collapse toggle */}
      <div className="flex h-14 items-center border-b px-3 shrink-0">
        <button
          onPointerDown={() => setOptimisticPath("/dashboard")}
          onClick={() => router.push("/dashboard")}
          className={cn(
            "flex items-center min-w-0 flex-1",
            collapsed && "justify-center"
          )}
          title="Dashboard"
        >
          <LogoLockup size={24} collapsed={collapsed} />
        </button>

        {!collapsed && (
          <button
            onClick={toggleCollapsed}
            title="Collapse sidebar"
            className="ml-auto shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search trigger */}
      {onSearchOpen && (
        <div className="px-2 pt-3 pb-1 shrink-0">
          <button
            onClick={onSearchOpen}
            title={collapsed ? "Search (⌘K)" : undefined}
            className={cn(
              "w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors",
              collapsed ? "justify-center" : ""
            )}
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className={cn("flex-1 text-left truncate transition-all duration-200 overflow-hidden whitespace-nowrap", collapsed ? "w-0 opacity-0" : "opacity-100")}>
              Search…
            </span>
            {!collapsed && (
              <kbd className="font-mono text-[10px] bg-muted rounded px-1 py-0.5 shrink-0">⌘K</kbd>
            )}
          </button>
        </div>
      )}

      {/* Nav items */}
      <nav aria-label="Primary" className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto min-h-0">
        {NAV_ITEMS.map((item) => {
          const active = isOptimisticActive(optimisticPath, item) || (!optimisticPath && isNavActive(pathname, item));
          return (
            <Link
              key={item.href}
              href={item.href}
              title={collapsed ? item.label : undefined}
              aria-current={active ? "page" : undefined}
              onMouseEnter={() => preloadRoute(item.href, router)}
              onTouchStart={() => preloadRoute(item.href, router)}
              onPointerDown={() => setOptimisticPath(item.href)}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                collapsed ? "justify-center px-0" : "",
                active
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className={cn("truncate transition-all duration-200 overflow-hidden whitespace-nowrap", collapsed ? "w-0 opacity-0" : "opacity-100")}>
                {item.label}
              </span>
            </Link>
          );
        })}

        {/* Super-admin only */}
        {isSuperAdmin && (
          <>
            {!collapsed && <div className="h-px bg-border my-1" />}
            {ADMIN_NAV_ITEMS.map((item) => {
              const active = isOptimisticActive(optimisticPath, item) || (!optimisticPath && isNavActive(pathname, item));
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  aria-current={active ? "page" : undefined}
                  onMouseEnter={() => preloadRoute(item.href, router)}
                  onTouchStart={() => preloadRoute(item.href, router)}
                  onPointerDown={() => setOptimisticPath(item.href)}
                  className={cn(
                    "flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    collapsed ? "justify-center px-0" : "",
                    active
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className={cn("truncate transition-all duration-200 overflow-hidden whitespace-nowrap", collapsed ? "w-0 opacity-0" : "opacity-100")}>
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </>
        )}
      </nav>

      {/* Bottom: user menu only */}
      <div className="border-t px-2 py-2 space-y-0.5 shrink-0">
        {/* Collapse toggle (when collapsed) */}
        {collapsed && (
          <button
            onClick={toggleCollapsed}
            title="Expand sidebar"
            className="w-full flex justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}

        {/* User menu — organizations, settings, sign out */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "w-full flex items-center rounded-md border bg-background px-2 py-1.5 text-sm hover:bg-accent transition-colors min-w-0",
                collapsed ? "justify-center gap-0 px-0 border-0" : "gap-2"
              )}
              title={collapsed ? displayName : undefined}
            >
              <Avatar className="h-6 w-6 shrink-0">
                <AvatarFallback className="text-xs">{userInitials}</AvatarFallback>
              </Avatar>
              {!collapsed && (
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-[13px] font-medium leading-tight">{displayName}</span>
                  {userEmail && (
                    <span className="block truncate text-[11.5px] leading-tight text-muted-foreground">{userEmail}</span>
                  )}
                </span>
              )}
              {!collapsed && <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="right" align="end" className="w-56">
            {/* Workspace switcher */}
            <DropdownMenuLabel>Organizations</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {organizations.length > 0 ? (
              organizations.map((org) => (
                <DropdownMenuItem
                  key={org.id}
                  onClick={async () => { try { await onSwitchOrg(org.id); } catch {} }}
                  className="flex items-center justify-between py-2"
                >
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{org.name}</span>
                    <span className="text-xs text-muted-foreground">{org.role}</span>
                  </div>
                  {org.id === currentOrg?.id && <Check className="h-4 w-4 shrink-0 text-primary" />}
                </DropdownMenuItem>
              ))
            ) : (
              <DropdownMenuItem disabled>No organizations</DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Support chat entry (the app has no floating bubble) */}
            {onChatOpen && (
              <DropdownMenuItem onClick={onChatOpen}>
                <MessageCircle className="mr-2 h-4 w-4" />
                Support Chat
              </DropdownMenuItem>
            )}

            {/* Settings */}
            <DropdownMenuItem onClick={() => router.push("/dashboard/settings")}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Sign out */}
            <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
